import { AttributionSummary } from 'utils/types/billingTypes';
import { TagType } from 'utils/types/enums';
import {
	APIAccountsReceivableVisit,
	AccountsReceivableVisit,
	BaseNote,
	ConsentDocumentsList,
	Differential,
	DischargeExistingInstruction,
	DischargeNewInstruction,
	DischargeNote,
	Encounter,
	Exam,
	ModifyReferralSourceBody,
	ModifyVisitBody,
	NewExamSection,
	OutgoingProblem,
	PatientHistory,
	PatientRecordVisit,
	ProblemList,
	SMSTextMessage,
	SendEmailRequest,
	SendSmsRequest,
	TRIAGE_LEVELS,
	VisitArTransitionRequest,
	VisitPageVisit,
	VisitTransitionRequest,
	convertToAPI,
	convertToLocal,
} from '../utils/dataTypes';
import { pimsApi } from './pimsApi';

interface AddProgressNoteParams {
	visitId: number;
	content: string;
	category: string;
	internal: boolean;
	note_date_time: number;
	author_id: number | null;
}

interface ModifyDischargeNoteParams extends Pick<BaseNote, 'content'> {
	visitId: number;
}

interface EditProgressNoteParams {
	visit_id: number;
	id: number;
	content: string;
	category: string;
	internal: boolean;
	note_date_time: number;
	author_id: number | null;
}

export enum PrefixChar {
	BURIAL = "a",
 	ESTIMATE = "b",
 	EUTHANASIA = "c",
 	CHECK_IN = "d",
 	WEB_CHECKOUT = "e",
 	MEDICATION_CONSENT = "f",
	RABIES_VACCINATION_FORM = "g",
}

// Define a service using a base URL and expected endpoints
export const visitApi = pimsApi.injectEndpoints({
	endpoints: (builder) => ({
		/**
		 * GET
		 */
		getVisitList: builder.query<VisitPageVisit[], null>({
			query: () => `visit`,
			providesTags: (result, error) => [{ type: TagType.Visit }],
			transformResponse: (response: { data: VisitPageVisit[] }) => {
				return response.data;
			},
		}),
		getVisitById: builder.query<PatientRecordVisit, number>({
			query: (visitId) => `visit/${visitId}`,
			providesTags: (result, error, id) => [{ type: TagType.Visit, id }],
			transformResponse: (response: {
				data: PatientRecordVisit;
			}): PatientRecordVisit => response.data,
		}),
		getEncountersByVisitId: builder.query<Encounter[], number>({
			query: (visitId) => `visit/${visitId}/encounter`,
			providesTags: (result, error, id) => [
				{ type: TagType.Encounter, id },
			],
			transformResponse: (response: { data: Encounter[] }) => {
				return response.data;
			},
		}),
		getExamsByVisitId: builder.query<Exam[], number>({
			query: (visitId) => `visit/${visitId}/exam`,
			providesTags: (result, error, id) => [{ type: TagType.Exam, id }],
			transformResponse: (response: { data: Exam[] }) =>
				response.data.reverse(),
		}),
		getExamById: builder.query<Exam, number>({
			query: (examId) => `exam/${examId}`,
			providesTags: (result, error, examId) => [{ type: TagType.Exam, id: examId }],
			transformResponse: (response: { data: Exam }) => response.data,
		}),
		getNotesByVisitId: builder.query<BaseNote[], number>({
			query: (visitId) => `visit/${visitId}/note`,
			providesTags: (result, error, id) => [
				{ type: TagType.ProgNote, id },
			],
			transformResponse: (response: { data: BaseNote[] }) =>
				response.data.reverse(),
		}),
		getNoteById: builder.query<BaseNote, number>({
			query: (noteId) => `note/${noteId}`,
			providesTags: (result, error, noteId) => [
				{ type: TagType.ProgNote, id: noteId },
			],
			transformResponse: (response: { data: BaseNote }) => response.data
		}),
		getDischargeNoteByVisitId: builder.query<DischargeNote, number>({
			query: (visitId) => `visit/${visitId}/dc_note`,
			providesTags: (result, error, id) => [
				{ type: TagType.DischargeNote, id },
			],
			transformResponse: (response: { data: DischargeNote }) =>
				response.data,
		}),
		getDischargeTogoMedsByVisitId: builder.query<
			DischargeExistingInstruction[],
			number
		>({
			query: (visitId) => `visit/${visitId}/togomeds`,
			providesTags: (result, error, visitId) => [
				{ type: TagType.DischargeMed, id: visitId },
			],
			transformResponse: (response: {
				data: DischargeExistingInstruction[];
			}) => {
				return response.data.map((togoMed) => {
					const convertedValues = convertToLocal(togoMed.dispense_value, togoMed.dispense_unit);

					return {
						...togoMed,
						togo: true,
						dispense_value: convertedValues.dose,
						dispense_unit: convertedValues.dose_unit
					};
				});
			},
		}),

		getProblemListByVisitId: builder.query<
			{ diffList: Differential[]; problemList: ProblemList[] },
			number
		>({
			query: (visitId) => `visit/${visitId}/problem`,
			providesTags: (result, error, id) => [
				{ type: TagType.Problem, id },
			],
			transformResponse: (response: { data: ProblemList[] }) => {
				const newDiffList = response.data
					.flatMap((problem) => problem.differentials)
					.filter((diff) => !!diff);

				let uniqueDiffs = [
					...new Set(newDiffList.map((item) => item.differential_id)),
				].map(
					(diff_id) =>
						newDiffList.find(
							(newDiff) => newDiff.differential_id === diff_id,
						) as Differential,
				);

				const diffCountMap: { [key: number]: number } =
					generateDiffOccuranceMap(response.data);

				uniqueDiffs = [...uniqueDiffs].map((diff) => ({
					...diff,
					count: diffCountMap[diff.differential_id],
				}));

				return { diffList: uniqueDiffs, problemList: response.data };
			},
		}),
		getPatientHistoryByVisitId: builder.query<PatientHistory, number>({
			query: (visitId) => `visit/${visitId}/medical_history`,
			providesTags: (result, error, id) => [
				{ type: TagType.PatientHistory, id },
			],
			transformResponse: (response: { data: PatientHistory }) => {
				return response.data;
			},
		}),
		getAccountsReceivableVisitList: builder.query<AccountsReceivableVisit[], null>({
			query: () => `visit/accounts_receivable`,
			providesTags: (result, error) => [{ type: TagType.Visit, id: 'LIST' }],
			transformResponse: (response: { data: APIAccountsReceivableVisit[] }): AccountsReceivableVisit[] => {
				return response.data.map((visit) => {
					return {
						visit_id: visit.visit_id,
						pet_id: visit.pet_id,
						pet_name: visit.pet_name,
						owner_name: `${visit.owner_first_name || ""} ${visit.owner_last_name || ""}`,
						created_at: visit.created_at,
						invoice_total: visit.invoice_total_cents,
						amount_due: visit.invoice_total_cents - visit.amount_total_cents,
						ar_status: visit.ar_status || "awaiting_payment",
					}
				});
			},
		}),
		/**
		 * Add/POST new
		 */
		addNewVisit: builder.mutation<
			PatientRecordVisit,
			{ patientId: string, status?: string }
		>({
			query({ patientId, status }) {
				return {
					url: `visit`,
					method: 'POST',
					body: {
						pet_id: patientId,
						status: status || 'arrived'
					},
				};
			},
			transformResponse: (response: { data: PatientRecordVisit }): PatientRecordVisit => response.data,
			invalidatesTags: (result, error,) => [
				{ type: TagType.Visit, id: 'LIST' },
				{ type: TagType.Visit, id: result?.id },
			],
		}),
		addNewProblem: builder.mutation<
			ProblemList,
			{ visitId: number; body: OutgoingProblem }
		>({
			query({ visitId, body }) {
				return {
					url: `visit/${visitId}/problem`,
					method: 'POST',
					body,
				};
			},

			invalidatesTags: (result, error, { visitId }) => [
				{ type: TagType.Problem, id: visitId },
			],
		}),
		addPhysicalExam: builder.mutation<
			Exam,
			{ visit_id: number; exam: NewExamSection[]; note: string, note_date_time: number }
		>({
			query({ visit_id, exam, note, note_date_time }) {
				return {
					url: `visit/${visit_id}/exam`,
					method: 'POST',
					body: { sections: exam, note, note_date_time },
				};
			},
			invalidatesTags: (result, error, { visit_id }) => [
				{ type: TagType.Exam, id: visit_id },
			],
			transformResponse: (response: { data: Exam }) => {
				return response.data;
			},
		}),
		editPhysicalExam: builder.mutation<
			Exam,
			{ id: number; visit_id: number; exam: NewExamSection[]; note: string, note_date_time: number }
		>({
			query({ id, exam, note, note_date_time }) {
				return {
					url: `exam/${id}`,
					method: 'PUT',
					body: { sections: exam, note, note_date_time },
				};
			},
			invalidatesTags: (result, error, { visit_id }) => [
				{ type: TagType.Exam, id: visit_id },
			],
		}),
		deletePhysicalExam: builder.mutation<
			void,
			{ visitId: number; id: number; }
		>({
			query({ id }) {
				return {
					url: `exam/${id}`,
					method: 'DELETE',
				};
			},
			invalidatesTags: (result, error, { visitId }) => [
				{ type: TagType.Exam, id: visitId },
			],
		}),
		createEditExamCache: builder.mutation<void, { examId: number; }>({
			query: ({ examId }) => ({
				url: `exam/${examId}/create_cache`,
				method: 'POST'
			}),
		}),
		validateEditExam: builder.mutation<void, { examId: number; }>({
			query: ({ examId }) => ({
				url: `exam/${examId}/validate`,
				method: 'POST'
			}),
		}),
		removeEditExamCache: builder.mutation<void, { examId: number; }>({
			query: ({ examId }) => ({
				url: `exam/${examId}/remove_cache`,
				method: 'POST'
			}),
		}),
		addProgressNote: builder.mutation<BaseNote, AddProgressNoteParams>({
			query({ visitId, ...rest }) {
				return {
					url: `visit/${visitId}/note`,
					method: 'POST',
					// TODO: Setting internal to false to deactivate private notes. Issue #424
					body: { ...rest, internal: false },
				};
			},
			invalidatesTags: (result, error, { visitId }) => [
				{ type: TagType.ProgNote, id: visitId },
			],
			transformResponse: (response: { data: BaseNote }) => {
				return response.data;
			},
		}),
		deleteProgressNote: builder.mutation<void, { visitId: number; id: number; }>({
			query: ({ id }) => ({
				url: `note/${id}`,
				method: 'DELETE',
			}),
			invalidatesTags: (result, error, { visitId }) => [
				{ type: TagType.ProgNote, id: visitId },
			],
		}),
		createEditNoteCache: builder.mutation<void, { note_id: number; }>({
			query: ({ note_id }) => ({
				url: `note/${note_id}/create_cache`,
				method: 'POST'
			}),
		}),
		validateEditNote: builder.mutation<void, { note_id: number; }>({
			query: ({ note_id }) => ({
				url: `note/${note_id}/validate`,
				method: 'POST'
			}),
		}),
		removeEditNoteCache: builder.mutation<void, { note_id: number; }>({
			query: ({ note_id }) => ({
				url: `note/${note_id}/remove_cache`,
				method: 'POST'
			}),
		}),
		addDischargeTogoMed: builder.mutation<
			DischargeExistingInstruction,
			{ visitId: number; body: DischargeNewInstruction }
		>({
			query({ visitId, body }) {
				const { name, togo, visit_id, related_instruction_id, userId, ...trimmedBody } = body;
				const convertedValues = convertToAPI(body.dispense_value, body.dispense_unit);
				const { dose_unit } = convertToAPI(body.dispense_value, body.dose_unit);

				return {
					url: `visit/${visitId}/togomed`,
					method: 'POST',
					body: {
						...trimmedBody,
						dispense_value: convertedValues.dose,
						dispense_unit: convertedValues.dose_unit,
						dose_unit,
						ordered_by: body.userId,
					},
				};
			},
			invalidatesTags: (result, error, { visitId }) => [
				{ type: TagType.DischargeMed, id: visitId },
			],
		}),
		/**
		 * Mutate/modify existing
		 */

		modifyVisit: builder.mutation<
			PatientRecordVisit,
			{ visitId: number; body: ModifyVisitBody }
		>({
			query({ visitId, body }) {
				return {
					url: `visit/${visitId}`,
					method: 'PUT',
					body,
				};
			},
			invalidatesTags: (result, error, { visitId }) => [
				{ type: TagType.Visit, id: visitId },
				{ type: 'rDVM' },
			],
			onQueryStarted(
				{ visitId, body, ...patch },
				{ dispatch, queryFulfilled },
			) {
				const visitPatchResult = dispatch(
					visitApi.util.updateQueryData(
						'getVisitById',
						visitId,
						(draft) => {
							Object.assign(draft, {
								customer_id: body.owner_id,
							});
						},
					),
				);
				queryFulfilled.catch(visitPatchResult.undo);
			},
		}),
		modifyReasonForVisit: builder.mutation<
			null,
			{ visitId: number; body: { reason_for_visit: string | null } }
		>({
			query({ visitId, body }) {
				return {
					url: `visit/${visitId}/reason_for_visit`,
					method: 'PUT',
					body,
				};
			},
			invalidatesTags: (result, error, { visitId }) => [
				{ type: TagType.Visit, id: visitId },
			],
		}),
		deleteProblem: builder.mutation<
			null,
			{ visitId: number; problem_id: number }
		>({
			query({ visitId, problem_id }) {
				return {
					url: `visit/${visitId}/problem/${problem_id}`,
					method: 'DELETE',
				};
			},
			invalidatesTags: (result, error, { visitId }) => [
				{ type: TagType.Problem, id: visitId },
			],
		}),
		modifyProblem: builder.mutation<
			ProblemList,
			{ visitId: number; body: ProblemList }
		>({
			query({ visitId, body }) {
				const { id, chronic, ...modifiedProblem } = body;
				return {
					url: `visit/${visitId}/problem/${id}`,
					method: 'PUT',
					body: modifiedProblem,
				};
			},
			invalidatesTags: (result, error, { visitId }) => [
				{ type: TagType.Problem, id: visitId },
			],
		}),
		modifyDiffStatus: builder.mutation<
			null,
			{
				visitId: number;
				differential_id: number;
				status: 'confirmed' | 'suspected' | 'ruled-out'; //TODO gotta be a way to streamline these constants...
			}
		>({
			query({ visitId, differential_id, status }) {
				return {
					url: `visit/${visitId}/differential/${differential_id}`,
					method: 'PUT',
					body: { status },
				};
			},
			invalidatesTags: (result, error, { visitId }) => [
				{ type: TagType.Problem, id: visitId },
			],
		}),
		modifyProgressNote: builder.mutation<BaseNote, EditProgressNoteParams>({
			query({ id, visit_id, ...rest }) {
				return {
					url: `note/${id}`,
					method: 'PUT',
					body: rest,
				};
			},
			invalidatesTags: (result, error, { visit_id, ...rest }) => {
				return [{ type: TagType.ProgNote, visit_id }];
			},
			transformResponse: (response: { data: BaseNote }) => {
				return response.data;
			},
		}),
		modifyDischargeNote: builder.mutation<
			BaseNote,
			ModifyDischargeNoteParams
		>({
			query({ visitId, ...rest }) {
				return {
					url: `visit/${visitId}/dc_note`,
					method: 'PUT',
					body: rest,
				};
			},
			invalidatesTags: (result, error, { visitId }) => [
				{ type: TagType.DischargeNote, id: visitId },
			],
		}),
		createEditDischargeNoteCache: builder.mutation<void, { visitId: number; }>({
			query: ({ visitId }) => ({
				url: `visit/${visitId}/dc_note/create_cache`,
				method: 'POST'
			}),
		}),
		validateEditDischargeNote: builder.mutation<void, { visitId: number; }>({
			query: ({ visitId }) => ({
				url: `visit/${visitId}/dc_note/validate`,
				method: 'POST'
			}),
		}),
		removeEditDischargeNoteCache: builder.mutation<void, { visitId: number; }>({
			query: ({ visitId }) => ({
				url: `visit/${visitId}/dc_note/remove_cache`,
				method: 'POST'
			}),
		}),
		modifyPatientHistory: builder.mutation<
			PatientHistory,
			{ content: string; visitId: number }
		>({
			query({ visitId, content }) {
				return {
					url: `visit/${visitId}/medical_history`,
					method: 'PUT',
					body: { content },
				};
			},
			invalidatesTags: (result, error, { visitId, ...rest }) => {
				return [{ type: TagType.PatientHistory, visitId }];
			},
		}),
		createEditPatientHistoryCache: builder.mutation<void, { visitId: number; }>({
			query: ({ visitId }) => ({
				url: `visit/${visitId}/medical_history/create_cache`,
				method: 'POST'
			}),
		}),
		validateEditPatientHistory: builder.mutation<void, { visitId: number; }>({
			query: ({ visitId }) => ({
				url: `visit/${visitId}/medical_history/validate`,
				method: 'POST'
			}),
		}),
		removeEditPatientHistoryCache: builder.mutation<void, { visitId: number; }>({
			query: ({ visitId }) => ({
				url: `visit/${visitId}/medical_history/remove_cache`,
				method: 'POST'
			}),
		}),
		modifyDischargeTogoMed: builder.mutation<
			DischargeExistingInstruction,
			{ visitId: number; body: DischargeExistingInstruction }
		>({
			query({ body }) {
				const {
					id,
					togo,
					related_instruction_id,
					medication_id,
					name,
					visit_id,
					created_at,
					created_by,
					action_status,
					dose_value,
					discontinued_at,
					discontinued_by,
					discontinued_on_behalf_of,
					discontinued_reason,
					...trimmedBody
				} = body;
				const convertedValues = convertToAPI(body.dispense_value, body.dispense_unit);

				return {
					url: `togomed/${id}`,
					method: 'PUT',
					body: {
						...trimmedBody,
						dispense_value: convertedValues.dose,
						dispense_unit: convertedValues.dose_unit,
					},
				};
			},
			invalidatesTags: (result, error, { visitId }) => [
				{ type: TagType.DischargeMed, id: visitId },
			],
		}),
		modifyFinalizeDischargeTogoMed: builder.mutation<
			DischargeExistingInstruction,
			{ visitId: number; togomed_id: number }
		>({
			query({ togomed_id }) {
				return {
					url: `togomed/${togomed_id}/finalize`,
					method: 'PUT',
				};
			},
			invalidatesTags: (result, error, { visitId }) => [
				{ type: TagType.DischargeMed, id: visitId },
			],
		}),
		modifyReferralSource: builder.mutation<
			null,
			{ visit_id: number; body: ModifyReferralSourceBody }
		>({
			query({ visit_id, body }) {
				return {
					url: `visit/${visit_id}/referral_source`,
					method: 'PUT',
					body,
				};
			},
			invalidatesTags: (result, error, { visit_id, body }) => [
				{ type: TagType.Visit, id: visit_id },
				{ type: 'Customer', id: body.customer_id },
			],
		}),
		modifyTriageLevel: builder.mutation<
			{ status: number | undefined },
			{ visit_id: number, triage_level: TRIAGE_LEVELS }
		>({
			query({ visit_id, triage_level }) {
				return {
					url: `visit/${visit_id}/triage_level`,
					method: 'PUT',
					body: { triage_level }
				}
			},
			transformResponse: (_, meta) => {
				return { status: meta?.response?.status }
			},
			invalidatesTags: (result, error, { visit_id, triage_level }) => {
				if (result?.status === 200) {
					return [
						{ type: TagType.Visit, id: visit_id }
					]
				} else {
					return []
				}
			}
		}),

		/**
		 * Delete/remove/discontinue
		 */

		removeDifferential: builder.mutation<
			null,
			{ visitId: number; id: number }
		>({
			query({ visitId, id }) {
				return {
					url: `visit/${visitId}/differential/${id}`,
					method: 'DELETE',
				};
			},
			invalidatesTags: (result, error, { visitId }) => [
				{ type: TagType.Problem, id: visitId },
			],
		}),

		/**
		 * Visit status changes
		 */
		transitionStatus: builder.mutation<
			null,
			{
				visitId: number;
			} & VisitTransitionRequest
		>({
			query({ visitId, status, hospitalization_level }) {
				return {
					url: `visit/${visitId}/transition`,
					method: 'POST',
					body: { status, hospitalization_level },
				};
			},
			invalidatesTags: (result, error, { visitId }) => [
				{ type: TagType.Visit, id: visitId },
				{ type: TagType.Visit, id: 'LIST' },
			],
		}),

		arTransitionStatus: builder.mutation<
			null,
			{
				visitId: number;
			} & VisitArTransitionRequest
		>({
			query({ visitId, status }) {
				return {
					url: `visit/${visitId}/ar_transition`,
					method: 'POST',
					body: { status },
				};
			},
			invalidatesTags: (result, error, { visitId }) => [
				{ type: TagType.Visit, id: visitId },
				{ type: TagType.Visit, id: 'LIST' },
			],
		}),

		finalizeVisit: builder.mutation<
			null,
			{
				visitId: number;
			}
		>({
			query({ visitId }) {
				return {
					url: `visit/${visitId}/finalize`,
					method: 'PUT'
				};
			},
			invalidatesTags: (result, error, { visitId }) => [
				{ type: TagType.Visit, id: visitId },
				{ type: TagType.Visit, id: 'LIST' },
			],
		}),

		reopenVisit: builder.mutation<
			null,
			{
				visitId: number;
			}
		>({
			query({ visitId }) {
				return {
					url: `visit/${visitId}/reopen`,
					method: 'PUT',
					body: {},
				};
			},
			invalidatesTags: (result, error, { visitId }) => [
				{ type: TagType.Visit, id: visitId },
				{ type: TagType.Visit, id: 'LIST' },
			],
		}),
		finalizeInvoice: builder.mutation<
			null,
			{
				visitId: number;
			}
		>({
			query({ visitId }) {
				return {
					url: `visit/${visitId}/finalize-invoice`,
					method: 'POST',
					body: {},
				};
			},
			invalidatesTags: (result, error, { visitId }) => [
				{ type: TagType.Visit, id: visitId },
				{ type: TagType.Visit, id: 'LIST' },
			],
		}),
		undoFinalizeInvoice: builder.mutation<
			null,
			{
				visitId: number;
			}
		>({
			query({ visitId }) {
				return {
					url: `visit/${visitId}/undo-finalize-invoice`,
					method: 'POST',
					body: {},
				};
			},
			invalidatesTags: (result, error, { visitId }) => [
				{ type: TagType.Visit, id: visitId },
				{ type: TagType.Visit, id: 'LIST' },
			],
		}),

		/* Post-visit actions */

		emailRecordToCustomer: builder.mutation<
			null,
			{
				visitId: number;
			} & SendEmailRequest
		>({
			query({ visitId, email_type }) {
				return {
					url: `visit/${visitId}/email/record/customer`,
					method: 'POST',
					body: { email_type }
				};
			}
		}),

		emailRecordToRdvm: builder.mutation<
			null,
			{
				visitId: number;
			} & SendEmailRequest
		>({
			query({ visitId, email_type }) {
				return {
					url: `visit/${visitId}/email/record/rdvm`,
					method: 'POST',
					body: { email_type }
				};
			}
		}),

		textSurvey: builder.mutation<
			null,
			{
				visitId: number;
			} & SendSmsRequest
		>({
			query({ visitId, sms_type }) {
				return {
					url: `visit/${visitId}/sms/survey`,
					method: 'POST',
					body: { sms_type }
				};
			},
			invalidatesTags: (result, error, { visitId }) => [
				{ type: 'ChatMessages', visitId },
			],
		}),

		textFollowUp: builder.mutation<
			null,
			{
				visitId: number;
			} & SendSmsRequest
		>({
			query({ visitId, sms_type }) {
				return {
					url: `visit/${visitId}/sms/followup`,
					method: 'POST',
					body: { sms_type }
				};
			},
			invalidatesTags: (result, error, { visitId }) => [
				{ type: 'ChatMessages', visitId },
			],
		}),

		// /* Visit consent forms */
		getAllConsentDocuments: builder.query<ConsentDocumentsList[], number>({
			query: (visitId) => `visit/${visitId}/consent_forms`,
			providesTags: (result, error, id) => [{ type: TagType.ConsentForm }],
			transformResponse: (response: { data: ConsentDocumentsList[] }) => {
				return response.data;
			},
		}),
		sendEuthanasiaForm: builder.mutation<null, number>({
			query: (visitId) => {
				return {
					url: `/visit/${visitId}/euthanasia_form`,
					method: 'POST',
				};
			},
			invalidatesTags: (result, error, visitId) => [
				{ type: TagType.ConsentForm },
				{ type: 'ChatMessages', visitId },
			],
		}),
		sendBurialForm: builder.mutation<null, number>({
			query: (visitId) => {
				return {
					url: `/visit/${visitId}/burial_form`,
					method: 'POST',
				};
			},
			invalidatesTags: (result, error, visitId) => [
				{ type: TagType.ConsentForm },
				{ type: 'ChatMessages', visitId },
			],
		}),
		createConsentForm: builder.mutation<null, { visitId: number; prefixChar: PrefixChar; }>({
			query: ({ prefixChar, visitId }) => {
				return {
					url: `/visit/${visitId}/consent/${prefixChar}`,
					method: 'POST',
				};
			},
			invalidatesTags: (result, error, visitId ) => [
				{ type: TagType.ConsentForm  },
				{ type: 'ChatMessages', visitId },
			],
		}),
		createAndSendConsentForm: builder.mutation<null, { visitId: number; prefixChar: PrefixChar; }>({
			query: ({ prefixChar, visitId }) => {
				return {
					url: `/visit/${visitId}/create_and_send/${prefixChar}`,
					method: 'POST',
				};
			},
			invalidatesTags: (result, error, visitId ) => [
				{ type: TagType.ConsentForm  },
				{ type: 'ChatMessages', visitId },
			],
		}),
		sendSmsConsentForm: builder.mutation<null, { visitId: number; formLinkId: string; }>({
			query: ({ formLinkId, visitId }) => {
				return {
					url: `/visit/${visitId}/send_sms/${formLinkId}`,
					method: 'POST',
				};
			},
			invalidatesTags: (result, error, visitId ) => [
				{ type: TagType.ConsentForm  },
				{ type: 'ChatMessages', visitId },
			],
		}),
		replaceEuthanasiaForm: builder.mutation<null, string>({
			query: (formLinkId) => {
				return {
					url: `/form_link/${formLinkId}/euthanasia_form_replacer`,
					method: 'POST',
				};
			},
			invalidatesTags: (result, error, visitId) => [
				{ type: TagType.ConsentForm },
				{ type: 'ChatMessages', visitId },
			],
		}),
		replaceBurialForm: builder.mutation<null, string>({
			query: (formLinkId) => {
				return {
					url: `/form_link/${formLinkId}/burial_form_replacer`,
					method: 'POST',
				};
			},
			invalidatesTags: (result, error, visitId) => [
				{ type: TagType.ConsentForm },
				{ type: 'ChatMessages', visitId },
			],
		}),
		replaceEstimateForm: builder.mutation<null, string>({
			query: (formLinkId) => {
				return {
					url: `/form_link/${formLinkId}/estimate_form_replacer`,
					method: 'POST',
				};
			},
			invalidatesTags: (result, error, visitId) => [
				{ type: TagType.ConsentForm },
				{ type: 'ChatMessages', visitId },
			],
		}),
		deactivateConsentForm: builder.mutation<null, string>({
			query: (formLinkId) => {
				return {
					url: `/form_link/${formLinkId}/deactivator`,
					method: 'POST',
				};
			},
			invalidatesTags: (result, error, visitId) => [
				{ type: TagType.ConsentForm },
			],
		}),
		getAttributionSummary: builder.query<AttributionSummary, { month: number; year: number; }>({
			query: ({ month, year }) => ({
				url: `/attribution_summary`,
				params: { month, year },
			}),
			providesTags: (result, error, { month, year }) => [
				{ type: TagType.AttributionSummary, month: month + year }
			],
			transformResponse: (response: { data: AttributionSummary }) => {
				return response.data;
			},
		}),
		createEuthanasiaFormAndSendSMS: builder.mutation<null, number>({
			query: (visitId) => {
				return {
					url: `visit/${visitId}/euthanasia_form/send_sms`,
					method: 'POST',
					body: {},
				};
			},

			invalidatesTags: (result, error, visitId) => [
				{ type: TagType.ConsentForm },
				{ type: 'ChatMessages', visitId },
			],
			transformResponse: (response: { data: null }) => {
				return response.data;
			},
		}),
		createBurialFormAndSendSMS: builder.mutation<null, number>({
			query: (visitId) => {
				return {
					url: `visit/${visitId}/burial_form/send_sms`,
					method: 'POST',
					body: {},
				};
			},

			invalidatesTags: (result, error, visitId) => [
				{ type: TagType.ConsentForm },
				{ type: 'ChatMessages', visitId },
			],
			transformResponse: (response: { data: null }) => {
				return response.data;
			},
		}),
		sendSMSChatMessage: builder.mutation<
			null,
			{ visitId: number; messageContent: string; }
		>({
			query: ({ messageContent, visitId }) => {
				return {
					url: `visit/${visitId}/send_chat_message`,
					method: 'POST',
					body: {
						text_body: messageContent
					},
				};
			},
			invalidatesTags: (result, error, { visitId }) => [
				{ type: 'ChatMessages', visitId },
			],
			transformResponse: (response: { data: null }) => {
				return response.data;
			},
		}),
		getChatMessages: builder.query<SMSTextMessage[], number>({
			query: (visitId) => `visit/${visitId}/list_messages`,
			providesTags: () => [{ type: 'ChatMessages' }],
			transformResponse: (response: { data: SMSTextMessage[] }) => {
				return response.data;
			},
		}),
		createEstimateForm: builder.mutation<
			string,
			{
				visitId: number
				estimateId: number;
			}
		>({
			query: ({ visitId, estimateId }) => {
				return {
					url: `/visit/${visitId}/estimate/${estimateId}/estimate_form`,
					method: 'POST',
					body: {},
				};
			},

			invalidatesTags: (result, error) => [
				{ type: TagType.ConsentForm },
			],
			transformResponse: (response: { data: string }) => {
				return response.data;
			},
		}),
		createEstimateFormAndSendSMS: builder.mutation<
			null,
			{
				visitId: number
				estimateId: number;
			}
		>({
			query: ({ visitId, estimateId }) => {
				return {
					url: `/visit/${visitId}/estimate/${estimateId}/send_sms`,
					method: 'POST',
					body: {},
				};
			},

			invalidatesTags: (result, error) => [
				{ type: TagType.ConsentForm },
			],
			transformResponse: (response: { data: null }) => {
				return response.data;
			},
		}),

	}),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints

export const {
	// GET
	useGetVisitListQuery,
	useGetVisitByIdQuery,
	useGetNotesByVisitIdQuery,
	useGetDischargeNoteByVisitIdQuery,
	useGetDischargeTogoMedsByVisitIdQuery,
	useLazyGetDischargeTogoMedsByVisitIdQuery,
	useGetProblemListByVisitIdQuery,
	useGetEncountersByVisitIdQuery,
	useGetExamsByVisitIdQuery,
	useGetPatientHistoryByVisitIdQuery,
	useGetAccountsReceivableVisitListQuery,
	useGetAllConsentDocumentsQuery,
	useLazyGetAttributionSummaryQuery,
	useGetChatMessagesQuery,
	useGetNoteByIdQuery,
	useGetExamByIdQuery,

	// CREATE

	useAddNewProblemMutation,
	useAddPhysicalExamMutation,
	useAddProgressNoteMutation,
	useAddDischargeTogoMedMutation,
	useAddNewVisitMutation,
	useSendEuthanasiaFormMutation,
	useSendBurialFormMutation,
	useCreateEuthanasiaFormAndSendSMSMutation,
	useCreateBurialFormAndSendSMSMutation,
	useSendSMSChatMessageMutation,
	useCreateEstimateFormAndSendSMSMutation,
	useCreateEstimateFormMutation,
	useCreateConsentFormMutation,
	useCreateAndSendConsentFormMutation,


	// UPDATE
	useEditPhysicalExamMutation,
	useModifyVisitMutation,
	useModifyReasonForVisitMutation,
	useModifyDiffStatusMutation,
	useModifyProblemMutation,
	useModifyProgressNoteMutation,
	useModifyDischargeNoteMutation,
	useModifyPatientHistoryMutation,
	useModifyDischargeTogoMedMutation,
	useModifyFinalizeDischargeTogoMedMutation,
	useModifyReferralSourceMutation,
	useModifyTriageLevelMutation,
	useReplaceEuthanasiaFormMutation,
	useReplaceBurialFormMutation,
	useReplaceEstimateFormMutation,

	// Delete
	useRemoveDifferentialMutation,
	useDeactivateConsentFormMutation,
	useDeleteProblemMutation,
	useDeleteProgressNoteMutation,
	useDeletePhysicalExamMutation,

	// MISC
	useTransitionStatusMutation,
	useArTransitionStatusMutation,
	useFinalizeVisitMutation,
	useReopenVisitMutation,
	useUndoFinalizeInvoiceMutation,
	useFinalizeInvoiceMutation,
	useEmailRecordToCustomerMutation,
	useEmailRecordToRdvmMutation,
	useTextSurveyMutation,
	useTextFollowUpMutation,
	useSendSmsConsentFormMutation,

	// Cache validations
	useCreateEditNoteCacheMutation,
	useValidateEditNoteMutation,
	useRemoveEditNoteCacheMutation,
	useCreateEditExamCacheMutation,
	useValidateEditExamMutation,
	useRemoveEditExamCacheMutation,
	useCreateEditPatientHistoryCacheMutation,
	useValidateEditPatientHistoryMutation,
	useRemoveEditPatientHistoryCacheMutation,
	useCreateEditDischargeNoteCacheMutation,
	useValidateEditDischargeNoteMutation,
	useRemoveEditDischargeNoteCacheMutation,
} = visitApi;

const generateDiffOccuranceMap = (problemList: ProblemList[]) => {
	const diffCountMap: { [key: number]: number } = {};

	problemList.forEach((problem) => {
		problem?.differentials?.forEach((differential) => {
			if (!diffCountMap[differential.differential_id]) {
				diffCountMap[differential.differential_id] = 0;
			}
			diffCountMap[differential.differential_id]++;
		});
	});

	return diffCountMap;
};
